import { ArrowRight, MessageCircle, Scale } from "lucide-react";

// Premium law firm hero
const HERO_BG = "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80";

export const HeroSection = () => {
  return (
    <section
      id="home"
      className="relative min-h-[90vh] md:min-h-screen flex items-center"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/90 to-[#0A0A0B]/70" />
      
      {/* Gold accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-24 md:py-32">
        <div className="max-w-3xl">
          {/* Pre-headline with icon */}
          <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="w-10 h-10 md:w-12 md:h-12 border border-[#D4AF37]/30 flex items-center justify-center">
              <Scale className="w-5 h-5 md:w-6 md:h-6 text-[#D4AF37]" />
            </div>
            <div>
              <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-semibold block">
                4 ACES Attorneys
              </span>
              <span className="text-white/50 text-[10px] md:text-xs tracking-wider hidden sm:block">
                Private Property & Real Estate Law
              </span>
            </div>
          </div>

          {/* Main Headline */}
          <h1
            className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white leading-[1.1] mb-6 md:mb-8"
            data-testid="hero-headline"
          >
            Strategic Property Counsel
            <span className="block text-[#D4AF37] italic">for Investors in Nigeria</span>
          </h1>

          {/* Subheadline */}
          <p
            className="text-base md:text-lg lg:text-xl text-white/60 max-w-xl mb-8 md:mb-10 leading-relaxed font-light"
            data-testid="hero-subheadline"
          >
            Protecting local and diaspora investors through rigorous legal due diligence, 
            title verification, and structured property acquisition advisory.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-5">
            <a
              href="#booking"
              className="group inline-flex items-center justify-center gap-2 md:gap-3 bg-[#D4AF37] text-[#0A0A0B] px-6 md:px-10 py-4 md:py-5 text-xs md:text-sm tracking-[0.1em] md:tracking-[0.15em] font-bold hover:bg-[#C5A028] transition-all duration-300"
              data-testid="hero-cta-book"
            >
              Book a Private Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="https://wa.me/2348032143826?text=Hello%204ACES%20Attorneys%2C%20I%20saw%20your%20website%20and%20would%20like%20to%20discuss%20a%20property%20transaction%20in%20Nigeria."
              className="group inline-flex items-center justify-center gap-2 md:gap-3 bg-[#25D366] text-white px-6 md:px-10 py-4 md:py-5 text-xs md:text-sm tracking-[0.1em] md:tracking-[0.15em] font-bold hover:bg-[#20BD5A] transition-all duration-300"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Enquiry
            </a>
          </div>
          
          {/* Trust line */}
          <p className="text-white/50 text-sm md:text-base italic mb-10 md:mb-12">
            Schedule a confidential consultation regarding your property investment.
          </p>

          {/* Hero Credibility Strip */}
          <div className="flex flex-wrap items-center gap-4 md:gap-6 pt-8 md:pt-10 border-t border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
              <span className="text-white/50 text-xs md:text-sm tracking-wide">Property Due Diligence</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
              <span className="text-white/50 text-xs md:text-sm tracking-wide">Title Verification</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
              <span className="text-white/50 text-xs md:text-sm tracking-wide">Diaspora Property Advisory</span>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="h-16 md:h-24 bg-gradient-to-t from-[#FAFAF9] to-transparent" />
      </div>
      
      {/* Side accent - hidden on mobile */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-px h-48 bg-gradient-to-b from-transparent via-[#D4AF37]/50 to-transparent hidden lg:block" />
    </section>
  );
};
